/**
 * Athlete API utility functions
 */
import { buildApiUrl } from './apiConfig';
import { apiClient } from "./apiClient";
import { mutationErrorFromBody } from "./apiErrorMessage";
import { CompanyAthleteResponse } from "../types/athletes";

function messagesFromValidationMap(obj: unknown): string | undefined {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
    const msgs = Object.values(obj as Record<string, unknown>).filter(
        (v): v is string => typeof v === 'string' && v.trim().length > 0
    );
    return msgs.length > 0 ? msgs.join(' ') : undefined;
}

/** Prefer per-field messages from Problem JSON; fall back to detail / legacy Spring text. */
function extractProfileUpdateErrorMessage(errorData: Record<string, unknown>): string {
    const fromFields =
        messagesFromValidationMap(errorData.validationErrors) ?? messagesFromValidationMap(errorData.errors);
    if (fromFields) return fromFields;

    let errorMessage =
        (typeof errorData.detail === 'string' && errorData.detail) ||
        (typeof errorData.message === 'string' && errorData.message) ||
        (typeof errorData.error === 'string' && errorData.error);
    if (errorMessage && errorMessage.includes('default message [')) {
        const match = errorMessage.match(/default message \[([^\]]+)\]/);
        if (match) return match[1];
    }
    return typeof errorMessage === 'string' ? errorMessage : '';
}

export interface AthleteProfileResponse {
    id: number;
    firstName: string;
    lastName: string;
    displayName: string;
    title: string;
    /** User id of assigned team lead */
    staffId?: number;
    /** Staff/Administrator profile id for Team Lead assignment */
    staffProfileId?: number;
    staffName?: string;
    telephoneNumber?: string;
    imageUrl?: string; // Direct S3 URL from backend
    email: string;
    stateName?: string;
    sport?: Sport | string;
    /** Present when the account signs in with Clever; readonly display only. */
    cleverId?: string | null;
    cleverDistrictName?: string | null;
    authProvider?: string;
}

export interface Sport {
    id: number;
    name: string;
    category?: string;
    sortOrder?: number;
}

export interface SportCategoryGroup {
    category: string;
    sports: Sport[];
}

/** Groups sports by category, preserving API order within each group. */
export function groupSportsByCategory(sports: Sport[]): SportCategoryGroup[] {
    const groups: SportCategoryGroup[] = [];
    const indexByCategory = new Map<string, number>();

    for (const sport of sports) {
        const category = sport.category?.trim() || 'Sports';
        const existingIndex = indexByCategory.get(category);
        if (existingIndex === undefined) {
            indexByCategory.set(category, groups.length);
            groups.push({ category, sports: [sport] });
        } else {
            groups[existingIndex].sports.push(sport);
        }
    }

    return groups;
}

/**
 * Fetch the current logged-in athlete's profile using the /api/athletes/me endpoint
 * This is called when the profile form loads, not during login
 * @returns Promise with athlete profile data
 */
export async function fetchCurrentAthleteProfile(): Promise<AthleteProfileResponse | null> {
    try {
        const response = await apiClient(buildApiUrl('api/athletes/me'), {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            },
            credentials: 'include'
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();

        // Store athlete ID in localStorage for future profile updates
        if (data.id) {
            localStorage.setItem('athleteId', String(data.id));
        }

        return data;
    } catch (error) {
        return null;
    }
}

/**
 * Update athlete profile
 * For athletes editing their own profile: POST /api/athletes/profile
 * For admins editing an athlete profile: PUT /api/athletes/{email}/{parentUserId}
 * @param athleteId - The athlete ID
 * @param profileData - The profile data to update (must include email and parentUserId for admin updates)
 * @param isAthleteEditingOwnProfile - Whether the athlete is editing their own profile
 * @returns Promise with the response
 */
export async function updateAthleteProfile(athleteId: string | number, profileData: any, isAthleteEditingOwnProfile: boolean): Promise<{ success: boolean; error?: string }> {

    try {
        let updateAthleteEndpoint = '';
        let endpointMethod = '';

        let payload = profileData;

        if (isAthleteEditingOwnProfile) {
            updateAthleteEndpoint = `api/athletes/profile`;
            endpointMethod = 'POST';
            const { staffId, staffName, parentUserId, ...athleteSelfPayload } = profileData;
            payload = athleteSelfPayload;
        } else {
            // Admin updating athlete profile - use PUT /athletes/{email}/{parentUserId}
            const email = profileData.email;
            const parentUserId = profileData.parentUserId;

            if (!email || !parentUserId) {
                return { success: false, error: 'Email and parentUserId are required for admin updates' };
            }

            updateAthleteEndpoint = `api/athletes/${encodeURIComponent(email)}/${parentUserId}`;
            endpointMethod = 'PUT';
        }

        const response = await apiClient(buildApiUrl(updateAthleteEndpoint), {
            method: endpointMethod,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            try {
                const errorData = (await response.json()) as Record<string, unknown>;
                const errorMessage = extractProfileUpdateErrorMessage(errorData);
                return { success: false, error: errorMessage || `Failed to update profile (${response.status})` };
            } catch (jsonError) {
                return { success: false, error: `Failed to update profile (${response.status})` };
            }
        }

        return { success: true };
    } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
}

/**
 * Fetch available sports from the backend
 * @returns Promise with array of sports or null on error
 */
export async function fetchSports(): Promise<Sport[] | null> {
    try {
        const url = buildApiUrl('api/athletes/view');

        const response = await apiClient(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            },
            credentials: 'include'
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return null;
    }
}

const TECHNICAL_REPLACE_DETAIL =
    /must not be (blank|null)|HTTP\s*\d+|constraint-violation|Field error|SQL|Hibernate|Exception/i;

function friendlyGuardianReplaceError(status: number, detail: string, message = ""): string {
    if (status === 409 || /already used by another account/i.test(detail) || /parent\/guardian/i.test(detail)) {
        if (/must be different/i.test(detail) || /cannot be completed/i.test(detail)) {
            return detail;
        }
        return "That parent/guardian email is already used by another account. Please use a different email.";
    }
    if (status === 401) {
        return "Your session has expired. Please try again.";
    }
    if (status === 403) {
        return mutationErrorFromBody(
            403,
            { detail, message },
            "This update could not be verified. Please try again.",
        );
    }
    if (status === 404) {
        return "Athlete not found. They may have been deleted.";
    }
    if (detail && !TECHNICAL_REPLACE_DETAIL.test(detail)) {
        return detail;
    }
    return "Unable to update the parent/guardian email. Please try again.";
}

/**
 * Replace or remove one parent/guardian email on an existing athlete.
 * Omit {@code newEmail} to drop a second guardian when another remains.
 */
export async function replaceAthleteGuardianEmail(
    athleteProfileId: string | number,
    currentEmail: string,
    newEmail?: string | null,
): Promise<CompanyAthleteResponse> {
    const url = buildApiUrl(`api/athletes/${athleteProfileId}/guardians/replace`);
    const body: { currentEmail: string; newEmail?: string } = { currentEmail };
    const trimmedNew = (newEmail ?? "").trim();
    if (trimmedNew) {
        body.newEmail = trimmedNew;
    }

    const response = await apiClient(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        let detail = "";
        let message = "";
        try {
            const errorData = await response.json();
            detail = typeof errorData?.detail === "string" ? errorData.detail : "";
            message = typeof errorData?.message === "string" ? errorData.message : "";
        } catch {
            detail = "";
        }
        throw new Error(friendlyGuardianReplaceError(response.status, detail, message));
    }

    return response.json();
}

